import { resolveFrequencyFromKey } from "./publishingAutomationFrequency";
import {
  AUTOMATION_EFFECTIVE_STATUS,
  isAutomationSwitchOn,
} from "./publishingAutomationStatus";
import { formatNextPost, summarizeHeroMetrics } from "./nextScheduledAt";
import {
  getSelectedAccountKeys,
  parseAccountKey,
} from "./sharePlatformAccounts";
import {
  DEFAULT_VALIDITY_END_TIME,
  DEFAULT_VALIDITY_START_TIME,
  END_MODE_ALWAYS_ON,
  END_MODE_SCHEDULED,
  applyWallTimeToDate,
  buildDefaultValidityFields,
  inferStartMode,
  isAutomationStartLocked,
  resolveFormActiveFrom,
  resolveFormActiveUntil,
  resolvePersistedActiveFrom,
  zonedPartsFromTimestamp,
} from "./publishingAutomationValidity";

const SOURCE_ALL = "all";

const TONES = ["night", "mountain", "black"];

const toneFromId = (id = "") => {
  let hash = 0;
  for (let index = 0; index < id.length; index += 1) {
    hash = (hash + id.charCodeAt(index)) % TONES.length;
  }
  return TONES[hash] || "night";
};

export const buildAccountsPayload = ({ platformSelections, accounts }) => {
  const selectedKeys = getSelectedAccountKeys(platformSelections);
  const accountsByKey = new Map(
    accounts.map((account) => [account.key, account])
  );

  return selectedKeys.reduce((result, accountKey) => {
    const account = accountsByKey.get(accountKey);
    if (!account) return result;
    const parsed = parseAccountKey(account.key);
    result[accountKey] = {
      platform: account.platform,
      accountID:
        parsed.accountID ||
        account.account?.instagramID ||
        account.account?.id ||
        "",
      displayName: account.handle || "",
      enabled: true,
    };
    return result;
  }, {});
};

export const buildCreateAutomationPayload = ({
  formState,
  accounts,
  now = Date.now(),
}) => {
  const frequency = resolveFrequencyFromKey(formState.frequencyKey);
  const activeFrom = formState.startLocked
    ? formState.lockedActiveFrom
    : resolveFormActiveFrom({ formState, now });
  return {
    name: formState.name,
    source: {
      type: formState.sourceType,
      folderID:
        formState.sourceType === SOURCE_ALL ? null : formState.folderID,
      includeSubfolders:
        formState.sourceType === SOURCE_ALL
          ? false
          : Boolean(formState.includeSubfolders),
    },
    language: formState.language,
    accounts: buildAccountsPayload({
      platformSelections: formState.platforms,
      accounts,
    }),
    schedule: {
      frequencyKey: formState.frequencyKey,
      postsPerPeriod: frequency.postsPerPeriod,
      period: frequency.period,
      windowStart: formState.startTime,
      windowEnd: formState.endTime,
      timezone: formState.timezone,
      activeFrom,
      activeUntil: resolveFormActiveUntil({ formState }),
    },
  };
};

const uniquePlatformsFromAccounts = (accounts = {}) => {
  const platforms = Object.values(accounts)
    .filter((account) => account?.enabled !== false)
    .map((account) => account.platform)
    .filter(Boolean);
  return [...new Set(platforms)];
};

export const mapAutomationToCard = ({
  automationID,
  automation,
  runtime = {},
}) => {
  const effectiveStatus =
    runtime.effectiveStatus ||
    automation.desiredStatus ||
    AUTOMATION_EFFECTIVE_STATUS.PAUSED;

  return {
    id: automationID,
    name: automation.name,
    folderID: automation.source?.folderID || "",
    folderLabel: automation.source?.folderName || "",
    sourceType: automation.source?.type,
    includeSubfolders: Boolean(automation.source?.includeSubfolders),
    language: automation.language || "",
    frequencyKey: automation.schedule?.frequencyKey,
    nextScheduledAt: Number(runtime.nextScheduledAt) || null,
    nextPostLabel: formatNextPost(runtime.nextScheduledAt),
    contentRemaining: runtime.contentRemaining || 0,
    queuedCount: runtime.queuedCount || 0,
    publishedThisWeek: runtime.publishedThisWeek || 0,
    failedCount: runtime.failedCount || 0,
    lastFillAt: runtime.lastFillAt || null,
    attention: runtime.attention || null,
    active: isAutomationSwitchOn(effectiveStatus),
    effectiveStatus,
    desiredStatus: automation.desiredStatus,
    platforms: uniquePlatformsFromAccounts(automation.accounts),
    accounts: automation.accounts || {},
    schedule: automation.schedule || {},
    version: automation.version,
    tone: toneFromId(automationID),
    raw: automation,
  };
};

export const mapFormFromAutomation = (item) => {
  const platformSelections = Object.entries(item.accounts || {}).reduce(
    (result, [accountKey, account]) => {
      result[accountKey] = account?.enabled !== false;
      return result;
    },
    {}
  );
  const timezone = item.schedule?.timezone;
  const createdAt = item.raw?.createdAt;
  const defaults = buildDefaultValidityFields();
  const startLocked = isAutomationStartLocked({
    schedule: item.schedule,
    createdAt,
  });
  const persistedFrom = resolvePersistedActiveFrom({
    schedule: item.schedule,
    createdAt,
  });
  const fromParts = persistedFrom
    ? zonedPartsFromTimestamp({ timestamp: persistedFrom, timezone })
    : null;
  const until = Number(item.schedule?.activeUntil);
  const hasUntil = Number.isFinite(until) && until > 0;
  const untilParts = hasUntil
    ? zonedPartsFromTimestamp({ timestamp: until, timezone })
    : null;

  return {
    name: item.name || "",
    sourceType: item.sourceType || SOURCE_ALL,
    folderID: item.folderID || "",
    includeSubfolders: item.includeSubfolders !== false,
    language: item.language || item.raw?.language || "",
    platforms: platformSelections,
    frequencyKey: item.frequencyKey,
    startTime: item.schedule?.windowStart || "08:00",
    endTime: item.schedule?.windowEnd || "21:00",
    timezone,
    startMode: inferStartMode({ schedule: item.schedule, createdAt }),
    validityStartDate: applyWallTimeToDate(
      fromParts?.date || defaults.validityStartDate,
      DEFAULT_VALIDITY_START_TIME
    ),
    endMode: hasUntil ? END_MODE_SCHEDULED : END_MODE_ALWAYS_ON,
    validityEndDate: applyWallTimeToDate(
      untilParts?.date || defaults.validityEndDate,
      DEFAULT_VALIDITY_END_TIME
    ),
    startLocked,
    lockedActiveFrom: startLocked ? persistedFrom : null,
  };
};

export { formatNextPost, summarizeHeroMetrics };
